import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import type { useUpcomingMonths } from '@/features/billing/api'

export interface PaymentReservationSelection {
  readonly amountCents: number
  readonly contractId: string
  readonly periodMonth: string
}

interface PaymentReservationsProps {
  readonly query: ReturnType<typeof useUpcomingMonths>
  readonly selections: Readonly<Record<string, PaymentReservationSelection>>
  readonly onToggle: (selection: PaymentReservationSelection) => void
}

export function PaymentReservations({ query, selections, onToggle }: PaymentReservationsProps) {
  if (query.isPending) return <SkeletonRows testId="payment-reservations-loading" rows={2} />

  if (query.isError) {
    return (
      <ErrorState
        testId="payment-reservations-error"
        retryTestId="payment-reservations-retry"
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
      />
    )
  }

  const contracts = query.data.contracts.filter((contract) => contract.months.length > 0)

  if (contracts.length === 0) {
    return (
      <EmptyState
        compact
        testId="payment-reservations-empty"
        title="Keine weiteren Monate."
        hint="Alle Monate der nächsten zwölf sind schon vorgemerkt oder haben eine Sollstellung."
      />
    )
  }

  const showContractHeads = contracts.length > 1
  return (
    <section data-testid="payment-reservations" className="flex flex-col gap-2">
      <h3 className="text-label text-muted">Weitere Monate</h3>
      {contracts.map((contract) => (
        <div key={contract.contract.id} {...(showContractHeads ? { 'data-testid': `payment-reservations-contract-${contract.contract.id}` } : {})} className="flex flex-col gap-1">
          {showContractHeads && <p className="text-body text-text font-medium">{contract.subject.label}</p>}
          <div className="grid gap-2 md:grid-cols-2">
            {contract.months.map((month) => {
              const selection: PaymentReservationSelection = {
                amountCents: month.available_cents,
                contractId: contract.contract.id,
                periodMonth: month.period_month,
              }
              const key = `${selection.contractId}-${selection.periodMonth}`
              const selected = selections[key] !== undefined
              const unavailable = month.available_cents === 0
              return (
                <button
                  key={month.period_month}
                  type="button"
                  role="checkbox"
                  aria-checked={selected}
                  aria-disabled={unavailable}
                  disabled={unavailable}
                  data-testid={`payment-reservation-${contract.contract.id}-${month.period_month.slice(0, 7)}`}
                  onClick={() => {
                    onToggle(selection)
                  }}
                  className={`rounded-card flex min-h-(--tap-min) w-full items-start justify-between gap-3 border px-3 py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${selected ? 'border-accent' : 'border-border hover:bg-bg'} ${unavailable ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-body text-text whitespace-normal">{month.period_display}</span>
                    <span className="text-label text-muted">{month.available_cents === 0 ? 'vorgemerkt' : month.partial ? 'anteilig' : 'voraus'}</span>
                  </span>
                  <span data-numeric className="text-body text-text shrink-0 font-medium">{month.available_display}</span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
      <p data-testid="payment-reservations-hint" className="text-label text-muted">
        Offene Posten werden sofort beglichen. Vorgemerkte Monate werden am Monatsersten verrechnet, sobald ihre Forderung entsteht.
      </p>
    </section>
  )
}
